import React, { useRef, useEffect, useState, useCallback } from "react";
import { DiscoverImage } from "../hooks/useDiscoverImages";
import { useNavigate } from "react-router-dom";

// Simple icon components to replace Tabler icons
const IconCheck = ({ size = 24, className = "" }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width={size}
		height={size}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}
	>
		<polyline points="20 6 9 17 4 12"></polyline>
	</svg>
);

const IconAlertTriangle = ({ size = 24, className = "" }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width={size}
		height={size}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}
	>
		<path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
		<line x1="12" y1="9" x2="12" y2="13"></line>
		<line x1="12" y1="17" x2="12.01" y2="17"></line>
	</svg>
);

/* Debug icons commented out
const IconBug = ({ size = 24 }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width={size}
		height={size}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<rect x="8" y="6" width="8" height="14" rx="4"></rect>
		<path d="M19 7l-3 2"></path>
		<path d="M5 7l3 2"></path>
		<path d="M19 19l-3-2"></path>
		<path d="M5 19l3-2"></path>
		<path d="M12 6V2"></path>
	</svg>
);

const IconArrowDown = ({ size = 24 }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width={size}
		height={size}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<line x1="12" y1="5" x2="12" y2="19"></line>
		<polyline points="19 12 12 19 5 12"></polyline>
	</svg>
);
*/

interface FailedImageInfo {
	url: string;
	error: string;
	filename: string;
	fixedUrl?: string;
}

interface BentoImageGridProps {
	images: DiscoverImage[];
	onLoadMore: () => void;
	loading: boolean;
	hasMore: boolean;
	stats?: {
		totalCount: number;
		displayedCount: number;
		remainingCount: number;
	};
	onImageClick?: (image: DiscoverImage) => void;
}

const BentoImageGrid: React.FC<BentoImageGridProps> = ({
	images,
	onLoadMore,
	loading,
	hasMore,
	stats,
	onImageClick,
}) => {
	const navigate = useNavigate();
	const [columns, setColumns] = useState<DiscoverImage[][]>([]);
	const [columnCount, setColumnCount] = useState(3);
	const [failedImages, setFailedImages] = useState<FailedImageInfo[]>([]);
	const [showErrorDetails, setShowErrorDetails] = useState(false);
	const [showScrollDebug, setShowScrollDebug] = useState(false);
	const [scrollDebug, setScrollDebug] = useState({
		lastIntersection: null as Date | null,
		hasMore,
		loading,
		imagesCount: images.length,
		stats: { ...stats },
	});

	const observerRef = useRef<IntersectionObserver | null>(null);
	const loaderRef = useRef<HTMLDivElement>(null);

	const handleImageError = (url: string, filename: string, error: any) => {
		// Check if we already have this image in the failed list
		if (!failedImages.some((img) => img.url === url)) {
			const fixedUrl = getFixedUrl(url, filename);
			setFailedImages((prev) => [
				...prev,
				{
					url,
					error: error?.message || "Unknown error",
					filename,
					fixedUrl: fixedUrl !== url ? fixedUrl : undefined,
				},
			]);
		}
	};

	// Try to fix a URL that might have special characters
	const getFixedUrl = (url: string, filename: string): string => {
		try {
			// Check if the filename has problematic characters
			if (
				filename.includes("%") ||
				filename.includes("?") ||
				filename.includes("&") ||
				filename.includes("#") ||
				filename.includes("+") ||
				filename.includes(" ")
			) {
				// Extract the bucket and path from the URL
				const urlParts = url.split("/object/public/");
				if (urlParts.length === 2) {
					const baseUrl = urlParts[0];
					const pathParts = urlParts[1].split("/");
					const bucket = pathParts[0];
					const path = pathParts.slice(1).join("/");

					// Decode it first if it has encoded characters
					const hasEncodedChars = /%[0-9A-F]{2}/.test(path);
					let fixedPath;

					if (hasEncodedChars) {
						try {
							const decodedPath = decodeURIComponent(path);
							fixedPath = encodeURIComponent(decodedPath);
						} catch (e) {
							fixedPath = path
								.split("/")
								.map((segment) => encodeURIComponent(segment))
								.join("/");
						}
					} else {
						fixedPath = path
							.split("/")
							.map((segment) => encodeURIComponent(segment))
							.join("/");
					}

					return `${baseUrl}/object/public/${bucket}/${fixedPath}`;
				}
			}
		} catch (err) {
			// Just return the original URL if there's an error
		}

		return url;
	};

	// Update debug info when loading state or hasMore changes
	useEffect(() => {
		setScrollDebug((prev) => ({
			...prev,
			hasMore,
			loading,
			imagesCount: images.length,
			stats: { ...stats },
		}));
	}, [hasMore, loading, images.length, stats]);

	// Create columns for masonry layout based on image aspect ratios
	const distributeImagesIntoColumns = useCallback(() => {
		if (images.length === 0) return;

		// Create empty columns
		const newColumns: DiscoverImage[][] = Array.from(
			{ length: columnCount },
			() => [],
		);

		// Track the total "height" of each column
		const columnHeights = Array(columnCount).fill(0);

		// Distribute images by always adding to the shortest column
		images.forEach((image) => {
			// Find the shortest column
			const shortestColumnIndex = columnHeights.indexOf(
				Math.min(...columnHeights),
			);

			// Add image to that column
			newColumns[shortestColumnIndex].push(image);

			// Update the column height (using aspect ratio as a proxy for height)
			// We divide by aspect ratio because taller images have smaller aspect ratios
			columnHeights[shortestColumnIndex] += 1 / (image.aspectRatio || 1);
		});

		setColumns(newColumns);
	}, [images, columnCount]);

	// Set up intersection observer for infinite scroll
	const handleObserver = useCallback(
		(entries: IntersectionObserverEntry[]) => {
			const [entry] = entries;

			// Update debug state first
			setScrollDebug((prev) => ({
				...prev,
				lastIntersection: new Date(),
			}));

			// If the entry is intersecting (visible) and we're not loading and there's more to load
			if (entry?.isIntersecting && !loading && hasMore) {
				onLoadMore();
			}
		},
		[loading, hasMore, onLoadMore, images.length],
	);

	// Update columns when images change
	useEffect(() => {
		distributeImagesIntoColumns();
	}, [distributeImagesIntoColumns]);

	// Update columns when window resizes
	useEffect(() => {
		const handleResize = () => {
			const width = window.innerWidth;
			let count = 2; // Default for mobile

			if (width >= 1200) {
				count = 5; // Extra large screens
			} else if (width >= 992) {
				count = 4; // Large screens
			} else if (width >= 768) {
				count = 3; // Medium screens
			} else if (width < 480) {
				count = 2; // Small mobile screens
			}

			setColumnCount(count);
		};

		handleResize(); // Initial call
		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	// Set up and clean up the intersection observer
	useEffect(() => {
		const options = {
			root: null, // viewport
			rootMargin: "500px", // Trigger earlier before user actually sees the element
			threshold: 0.1, // Trigger when 10% of the element is visible
		};

		// Create the observer
		observerRef.current = new IntersectionObserver(handleObserver, options);

		// Observe the loader element
		if (loaderRef.current) {
			observerRef.current.observe(loaderRef.current);
		}

		// Clean up on unmount
		return () => {
			if (observerRef.current) {
				observerRef.current.disconnect();
			}
		};
	}, [handleObserver]);

	const handleTryOn = (image: DiscoverImage, e: React.MouseEvent) => {
		e.stopPropagation(); // Prevent triggering the onImageClick
		navigate('/try-on', { state: { selectedImage: image, fromDiscover: true } });
	};

	return (
		<div className="relative">
			{/* Debug toolbar */}
			{/* Debugging UI commented out
			<div className="fixed bottom-4 right-4 z-10 flex gap-2">
				{showScrollDebug && (
					<div className="bg-white dark:bg-gray-800 p-2 sm:p-3 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 text-[10px] sm:text-xs max-w-[200px] sm:max-w-none">
						<h4 className="font-bold text-xs sm:text-sm">Scroll Debug</h4>
						<div className="space-y-1 mt-1">
							<p>
								Has more:{" "}
								<span className={hasMore ? "text-green-500" : "text-red-500"}>
									{hasMore ? "Yes" : "No"}
								</span>
							</p>
							<p>
								Loading:{" "}
								<span
									className={loading ? "text-yellow-500" : "text-green-500"}
								>
									{loading ? "Yes" : "No"}
								</span>
							</p>
							<p>Images: {images.length}</p>
							{stats && (
								<>
									<p>Total available: {stats.totalCount}</p>
									<p>Displayed: {stats.displayedCount}</p>
									<p>Remaining: {stats.remainingCount}</p>
								</>
							)}
							<p>
								Last observed:{" "}
								{scrollDebug.lastIntersection?.toLocaleTimeString() || "Never"}
							</p>
						</div>
					</div>
				)}

				<button
					onClick={() => setShowScrollDebug(!showScrollDebug)}
					className="bg-blue-100 hover:bg-blue-200 text-blue-800 p-1.5 sm:p-2 rounded-full"
					title="Toggle scroll debug info"
				>
					<IconBug size={20} />
				</button>

				{hasMore && (
					<button
						onClick={onLoadMore}
						disabled={loading}
						className="bg-purple-100 hover:bg-purple-200 text-purple-800 p-1.5 sm:p-2 rounded-full disabled:opacity-50"
						title="Manually load more images"
					>
						<IconArrowDown size={20} />
					</button>
				)}
			</div>
			*/}

			{/* Main grid */}
			<div className="flex gap-2 xs:gap-3 sm:gap-4 w-full">
				{columns.map((column, columnIndex) => (
					<div
						key={columnIndex}
						className="flex-1 flex flex-col gap-2 xs:gap-3 sm:gap-4"
					>
						{column.map((image) => {
							const hasSpecialChars =
								image.filename.includes("%") ||
								image.filename.includes("?") ||
								image.filename.includes("&") ||
								image.filename.includes("#") ||
								image.filename.includes("+") ||
								image.filename.includes(" ");

							return (
								<div
									key={image.id}
									className="relative group cursor-pointer"
									onClick={() => onImageClick?.(image)}
								>
									{/* Commented out warning for special characters in filenames
									{hasSpecialChars && (
										<div
											className="absolute top-1 sm:top-2 right-1 sm:right-2 z-10 bg-yellow-500 text-white rounded-full p-0.5 sm:p-1"
											title="Has special characters in filename"
										>
											<IconAlertTriangle size={14} className="sm:hidden" />
											<IconAlertTriangle
												size={16}
												className="hidden sm:block"
											/>
										</div>
									)}
									*/}
									<img
										src={image.url}
										alt=""
										className="w-full h-auto object-cover rounded-lg"
										loading="lazy"
										onError={(e) =>
											handleImageError(image.url, image.filename, e)
										}
									/>
									<div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity duration-200"></div>
									
									{/* Try On button */}
									<div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
										<button
											onClick={(e) => handleTryOn(image, e)}
											className="bg-haus-dark-green hover:bg-haus-light-olive text-white font-medium px-3 py-1.5 rounded text-sm shadow-lg transform transition hover:scale-105"
										>
											Try On
										</button>
									</div>
								</div>
							);
						})}
					</div>
				))}
			</div>

			{/* Loading indicator and trigger for infinite scroll */}
			<div
				ref={loaderRef}
				className="flex justify-center my-4 sm:my-6 h-10 sm:h-12"
			>
				{loading && (
					<div className="loader text-sm sm:text-base">Loading...</div>
				)}
				{!loading && !hasMore && images.length > 0 && (
					<div className="flex items-center text-green-600 gap-1 text-xs sm:text-sm">
						<IconCheck size={16} className="sm:hidden" />
						<IconCheck size={20} className="hidden sm:block" />
						<span>All images loaded</span>
					</div>
				)}
				{!loading && hasMore && (
					<button
						onClick={onLoadMore}
						className="px-3 sm:px-4 py-1.5 sm:py-2 bg-haus-dark-green text-white rounded text-xs sm:text-sm hover:bg-haus-light-olive transition"
					>
						Load more
					</button>
				)}
			</div>

			{/* Error details panel */}
			{failedImages.length > 0 && (
				<div className="mt-4 sm:mt-6 p-3 sm:p-4 border border-red-200 rounded-lg bg-red-50 dark:bg-red-900/20 dark:border-red-800">
					<div className="flex justify-between items-center">
						<h3 className="text-red-800 dark:text-red-400 font-medium text-xs sm:text-sm">
							<span>
								{failedImages.length} image{failedImages.length > 1 ? "s" : ""}{" "}
								failed to load
							</span>
						</h3>
						<button
							onClick={() => setShowErrorDetails(!showErrorDetails)}
							className="text-red-600 dark:text-red-400 text-xs sm:text-sm hover:underline"
						>
							{showErrorDetails ? "Hide details" : "Show details"}
						</button>
					</div>

					{showErrorDetails && (
						<div className="mt-2 sm:mt-3 text-xs sm:text-sm">
							<p className="mb-2 text-red-700 dark:text-red-300">
								These images may have special characters in their filenames that
								need URL encoding.
							</p>
							<div className="space-y-2 sm:space-y-3 max-h-60 sm:max-h-80 overflow-y-auto">
								{failedImages.map((img, i) => (
									<div
										key={i}
										className="p-2 border border-red-200 dark:border-red-800 rounded bg-white dark:bg-gray-800"
									>
										<p>
											<strong>Filename:</strong> {img.filename}
										</p>
										<div className="mt-1 overflow-hidden">
											<p className="truncate">
												<strong>URL:</strong>{" "}
												<a
													href={img.url}
													target="_blank"
													rel="noreferrer"
													className="text-blue-600 dark:text-blue-400 hover:underline truncate"
												>
													{img.url}
												</a>
											</p>
											{img.fixedUrl && (
												<p className="truncate mt-1">
													<strong>Fixed URL:</strong>{" "}
													<a
														href={img.fixedUrl}
														target="_blank"
														rel="noreferrer"
														className="text-green-600 dark:text-green-400 hover:underline truncate"
													>
														{img.fixedUrl}
													</a>
												</p>
											)}
										</div>
										<p className="mt-1">
											<strong>Error:</strong> {img.error}
										</p>
									</div>
								))}
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default BentoImageGrid;
